<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useModalFocusTrap } from '../../../composables/useModalFocusTrap';

/**
 * Il prodotto ZDHC scelto e' gia' associato a un altro prodotto del periodo.
 *
 * **Un popup e non la riga d'errore in fondo alla sezione**: gli altri rifiuti
 * dicono "riprova" o "ricarica", e sotto un elenco di prodotti fra cui scegliere
 * una riga rossa si legge come un intoppo. Questo invece dice che la scelta non
 * si puo' fare **e perche'**, e va letto prima di continuare a scegliere.
 *
 * Il nome dell'altro prodotto e' il contenuto, non un ornamento: chi ha decine
 * di prodotti nel periodo, davanti a "esiste gia'", dovrebbe cercarlo a mano.
 * Arriva dal 409 dell'api, che e' l'unico posto che sa quale sia.
 *
 * Interpolato come **testo** e non con `v-html`: il nome lo scrive l'utente, in
 * un CSV o a mano, e passarlo per markup lo farebbe eseguire. `NoMatchModal`
 * ha lo stesso problema e lo risolve diversamente — il suo messaggio porta
 * markup, quindi resta su `v-html` ed e' il nome a passare per un escape.
 */

/* Emits */
const emits = defineEmits(['closeModal']);
/* Props */
const props = defineProps({
    // Il prodotto che tiene gia' l'associazione, come lo dichiara l'api:
    // `{ product_id, product_name }`. Il nome puo' mancare — la colonna e'
    // nullable — e allora il messaggio lo dice senza nominarlo, invece di
    // scrivere "null" a schermo.
    conflict: { required: true, type: Object }
});
/* Data */
const { t } = useI18n();
const store = useStore();
const dialog = ref(null);
/* Computed */
const name = computed( () =>
{
    const declared = props.conflict?.product_name;

    return typeof declared === 'string' && declared.trim() !== '' ? declared.trim() : null;
});
const message = computed( () => name.value
    ? t('associationConflictModal.body', store.state.locale).replace('_product', name.value)
    : t('associationConflictModal.bodyUnnamed', store.state.locale));
/* Methods */
const close = () => emits('closeModal');
/* Hooks */
// Fuoco iniziale, trappola sul Tab, `Esc` e fuoco restituito al pulsante che
// l'ha aperto: sono le stesse quaranta righe che serviranno al prossimo dialog,
// e stanno in un posto solo.
useModalFocusTrap(dialog, close);
</script>
<template>
    <div
        @click.prevent="close"
        class="fixed inset-0 p-6 bg-gray-1000 bg-opacity-90 z-40 overscroll-y-contain overflow-y-auto text-blue-500">
        <div class="flex flex-col w-full h-full items-center justify-center">
            <div
                @click.stop
                ref="dialog"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="association-conflict-title"
                aria-describedby="association-conflict-message"
                class="bg-white dark:bg-gray-900 dark:text-gray-400 border dark:border-gray-700 rounded p-5 flex flex-col space-y-4 w-full md:w-2/3 lg:w-1/2 2xl:w-1/3">
                <div class="flex items-start justify-between space-x-3">
                    <h2 id="association-conflict-title" class="text-xl flex items-center space-x-2 text-red-700 dark:text-red-300">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 h-6 fill-current flex-shrink-0" aria-hidden="true" focusable="false">
                            <path d="M12,2A10,10,0,1,0,22,12,10.011,10.011,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.009,8.009,0,0,1,12,20Zm.5-3.5a.75.75,0,1,1-.75-.75A.75.75,0,0,1,12.5,16.5ZM11.75,6.5a3,3,0,0,0-3,3,1,1,0,0,0,2,0,1,1,0,1,1,1,1,1,1,0,0,0-1,1v1.25a1,1,0,0,0,2,0v-.42a3,3,0,0,0-1-5.83Z"/>
                        </svg>
                        <span>{{ t('associationConflictModal.title', store.state.locale) }}</span>
                    </h2>
                    <button
                        :title="t('actions.close', store.state.locale)"
                        :aria-label="t('actions.close', store.state.locale)"
                        @click.prevent="close"
                        class="bg-transparent min-w-min flex items-center justify-center transform hover:rotate-90 duration-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" class="w-4 fill-current text-gray-800 dark:text-gray-400" aria-hidden="true" focusable="false">
                                <path d="M34.5,32L62.2,4.2c0.7-0.7,0.7-1.8,0-2.5c-0.7-0.7-1.8-0.7-2.5,0L32,29.5L4.2,1.8c-0.7-0.7-1.8-0.7-2.5,0
                                    c-0.7,0.7-0.7,1.8,0,2.5L29.5,32L1.8,59.8c-0.7,0.7-0.7,1.8,0,2.5c0.3,0.3,0.8,0.5,1.2,0.5s0.9-0.2,1.2-0.5L32,34.5l27.7,27.8
                                    c0.3,0.3,0.8,0.5,1.2,0.5c0.4,0,0.9-0.2,1.2-0.5c0.7-0.7,0.7-1.8,0-2.5L34.5,32z"/>
                            </svg>
                    </button>
                </div>
                <p id="association-conflict-message" class="text-sm">
                    {{ message }}
                </p>
                <div class="flex items-center justify-end">
                    <button
                        @click.prevent="close"
                        class="px-5 py-3 font-medium leading-5 text-center text-white bg-blue-500 hover:bg-blue-300 dark:hover:bg-blue-700 rounded duration-300 lg:w-auto focus:ring-2 focus:ring-blue-700 focus:ring-offset-2 dark:focus:ring-blue-300 dark:focus:ring-offset-gray-900">
                        {{ t('actions.close', store.state.locale) }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
